/**
 * Shared pieces, built 1:1 from the design file. Anything used on more than one screen lives here so the
 * patterns cannot drift apart — the design's matrix says the skeleton / error / permission states are
 * "build once, reuse", and this is where that promise is kept.
 */
import type { CSSProperties, ReactNode } from "react";

export const C = {
  accent: "#2E63F5",
  danger: "#C13A26",
  dangerLine: "#E8C4BC",
  dangerSoft: "rgba(193,58,38,.07)",
  warn: "#A87413",
  warnLine: "#E4D2A8",
  warnSoft: "rgba(240,167,46,.09)",
  good: "#0B7D50",
  goodSoft: "rgba(11,125,80,.1)",
} as const;

export const card: CSSProperties = {
  background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 16, overflow: "hidden",
};
export const cardPad: CSSProperties = { ...card, padding: "17px 18px", display: "flex", flexDirection: "column", gap: 12 };

export function CardHead({ title, sub, right }: { title: string; sub?: string; right?: ReactNode }) {
  return (
    <div style={{ padding: "13px 16px", borderBottom: "1px solid var(--line)", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ fontWeight: 800, fontSize: 14 }}>{title}</div>
        {sub && <div style={{ fontSize: 11.5, color: "var(--muted)", fontWeight: 600, marginTop: 1, lineHeight: 1.45 }}>{sub}</div>}
      </div>
      {right}
    </div>
  );
}

/**
 * Severity. THREE levels, and each has its own SHAPE — triangle, square, circle — so severity survives
 * greyscale, colour-blindness and a bad monitor. Colour is reinforcement, never the carrier.
 */
export function Sev({ level }: { level: 1 | 2 | 3 }) {
  if (level === 1) {
    return <span title="Severity 1 — money may be unaccounted for" style={{ width: 20, height: 20, flex: "none", background: C.danger, color: "#fff", fontSize: 9, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", clipPath: "polygon(50% 0,100% 100%,0 100%)", paddingTop: 5 }}>1</span>;
  }
  if (level === 2) {
    return <span title="Severity 2 — act this working day" style={{ width: 18, height: 18, flex: "none", background: C.warn, color: "#fff", fontSize: 9.5, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 4 }}>2</span>;
  }
  return <span title="Severity 3 — visible, no page" style={{ width: 18, height: 18, flex: "none", border: "2px solid var(--muted)", color: "var(--muted)", fontSize: 9.5, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "50%" }}>3</span>;
}

export const Tick = ({ size = 16, color = C.good }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.8" style={{ flex: "none" }}><path d="M20 6 9 17l-5-5" /></svg>
);
export const Warn = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={C.danger} style={{ flex: "none" }}><path d="M12 2 23 21H1L12 2Z" /><path d="M12 9.5v5M12 17.5v.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" /></svg>
);
export const Alert = ({ size = 15, color = C.danger }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" style={{ flex: "none" }}><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16.5v.5" /></svg>
);
export const Chevron = ({ dir = "right" }: { dir?: "right" | "down" }) => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" strokeWidth="2.4" style={{ flex: "none" }}>
    <path d={dir === "right" ? "m9 6 6 6-6 6" : "m6 9 6 6 6-6"} />
  </svg>
);

/** Buttons. A viewer sees them DISABLED with the reason appended — never hidden (design decision 3). */
export function ActionButton({ children, onClick, locked, tone = "primary", small }: {
  children: ReactNode; onClick?: () => void; locked?: boolean; tone?: "primary" | "danger" | "quiet"; small?: boolean;
}) {
  const base: CSSProperties = {
    padding: small ? "9px 13px" : "12px 16px", borderRadius: small ? 10 : 11,
    fontSize: small ? 12 : 12.5, fontWeight: 800, cursor: locked ? "not-allowed" : "pointer", border: "none",
  };
  const style: CSSProperties = locked
    ? { ...base, border: "1px solid var(--line2)", background: "var(--subtle)", color: "var(--muted)" }
    : tone === "danger" ? { ...base, background: C.danger, color: "#fff" }
    : tone === "quiet" ? { ...base, border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink2)" }
    : { ...base, background: C.accent, color: "#fff" };
  return (
    <button type="button" onClick={locked ? undefined : onClick} disabled={locked} style={style}
      title={locked ? "Manager role required" : undefined}>
      {children}{locked ? " \u{1F512} Manager role required" : ""}
    </button>
  );
}

export function Skeleton({ rows = 4, label }: { rows?: number; label: string }) {
  return (
    <div style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: 9 }}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} style={{ height: 38, background: "var(--subtle)", borderRadius: 10, animation: `bp-shim 1.4s infinite ${i * 0.15}s` }} />
      ))}
      <div style={{ fontSize: 11.5, color: "var(--muted)", fontWeight: 700 }}>{label}</div>
    </div>
  );
}

/** Empty is often GOOD NEWS here. `tone="good"` gives it the green mark rather than a broken-page feel. */
export function EmptyState({ title, body, tone = "neutral", action }: {
  title: string; body: string; tone?: "neutral" | "good"; action?: ReactNode;
}) {
  return (
    <div style={{ padding: "30px 20px", display: "flex", flexDirection: "column", alignItems: "center", gap: 8, textAlign: "center" }}>
      {tone === "good" && (
        <div style={{ width: 38, height: 38, borderRadius: "50%", background: C.goodSoft, display: "flex", alignItems: "center", justifyContent: "center" }}><Tick size={18} /></div>
      )}
      <div style={{ fontWeight: 800, fontSize: 14 }}>{title}</div>
      <div style={{ fontSize: 12.5, color: "var(--muted)", fontWeight: 600, maxWidth: 420, lineHeight: 1.55 }}>{body}</div>
      {action}
    </div>
  );
}

export function ErrorState({ title, body, onRetry }: { title: string; body: string; onRetry?: () => void }) {
  return (
    <div style={{ padding: "30px 20px", display: "flex", flexDirection: "column", alignItems: "center", gap: 8, textAlign: "center" }}>
      <Alert size={22} />
      <div style={{ fontWeight: 800, fontSize: 14 }}>{title}</div>
      <div style={{ fontSize: 12.5, color: "var(--muted)", fontWeight: 600, maxWidth: 400, lineHeight: 1.5 }}>{body}</div>
      {onRetry && <ActionButton small onClick={onRetry}>Retry</ActionButton>}
    </div>
  );
}

/** A balance is NEVER shown without its as-of time; a stale one is demoted, not silently passed off. */
export function StaleBadge({ asOf }: { asOf: string }) {
  return (
    <span style={{ fontSize: 10.5, fontWeight: 800, color: C.warn, background: C.warnSoft, border: `1px solid ${C.warnLine}`, borderRadius: 6, padding: "2px 7px", alignSelf: "flex-start" }}>
      as of {asOf} — could not refresh
    </span>
  );
}

export function Note({ children }: { children: ReactNode }) {
  return (
    <div style={{ border: "1px dashed #C9D9FC", background: "var(--accentsoft)", borderRadius: 13, padding: "12px 14px", display: "flex", gap: 9, alignItems: "flex-start" }}>
      <span className="bp-mono" style={{ fontSize: 9.5, fontWeight: 700, color: C.accent, letterSpacing: ".08em", border: "1px solid #C9D9FC", padding: "2px 6px", borderRadius: 5, flex: "none", marginTop: 1 }}>NOTE</span>
      <span style={{ fontSize: 12, lineHeight: 1.55, color: "var(--ink2)" }}>{children}</span>
    </div>
  );
}

export function Banner({ tone, children, action }: { tone: "danger" | "warn" | "info" | "good"; children: ReactNode; action?: ReactNode }) {
  const map = {
    danger: { border: C.dangerLine, bg: C.dangerSoft },
    warn: { border: C.warnLine, bg: C.warnSoft },
    info: { border: "var(--line2)", bg: "var(--accentsoft)" },
    good: { border: "var(--line)", bg: C.goodSoft },
  }[tone];
  return (
    <div style={{ border: `1px solid ${map.border}`, background: map.bg, borderRadius: 12, padding: "11px 13px", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
      <span style={{ flex: 1, minWidth: 220, fontSize: 12.5, lineHeight: 1.55, color: "var(--ink2)" }}>{children}</span>
      {action}
    </div>
  );
}

/** Side drawer — the house pattern for anything that creates or decides (never a new page). */
export function Drawer({ title, onClose, children, width = 440 }: { title: string; onClose: () => void; children: ReactNode; width?: number }) {
  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(18,20,28,.45)", zIndex: 60 }} />
      <div role="dialog" aria-modal="true" aria-label={title} style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: `min(${width}px, 92vw)`, background: "var(--surface)", borderLeft: "1px solid var(--line)", zIndex: 61, padding: "22px 20px", display: "flex", flexDirection: "column", gap: 14, overflow: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontWeight: 800, fontSize: 15 }}>{title}</div>
          <button type="button" onClick={onClose} aria-label="Close" style={{ border: "none", background: "none", color: "var(--muted)", fontSize: 18, cursor: "pointer" }}>✕</button>
        </div>
        {children}
      </div>
    </>
  );
}

export const label: CSSProperties = { display: "flex", flexDirection: "column", gap: 5 };
export const labelText: CSSProperties = { fontSize: 12, fontWeight: 700 };
export const input: CSSProperties = { padding: "11px 12px", border: "1px solid var(--line2)", borderRadius: 10, background: "var(--subtle)", color: "var(--ink)", fontSize: 13 };
export const colHead: CSSProperties = { fontSize: 10.5, fontWeight: 800, color: "var(--muted)", letterSpacing: ".05em" };
